import { MoreVert as MoreVertIcon } from '@mui/icons-material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Divider,
    Link,
    List,
    ListItemText,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import CustomButton from 'components/UI/buttons/CustomButton.style';
import styles from 'features/DefaultHelp.module.css';
import YardCheckStatusChip from 'features/yardCheck/components/YardCheckStatusChip/YardCheckStatusChip';
import { useCallback, useMemo } from 'react';
import { YARD_CHECK_ITEM_TYPE } from 'utils/constants';
const { MISSING, NEW, MATCHED, UPDATED } = YARD_CHECK_ITEM_TYPE;

const CheckingContent = () => {
    const instructions = useMemo(
        () => [
            { key: 'searchAndAdd', title: 'Search and Select Assets' },
            { key: 'manuallyAdd', title: 'Add New Assets' },
            { key: 'removeItems', title: 'Remove Assets' },
            { key: 'updateInfo', title: 'Update Assets' },
        ],
        []
    );

    const renderOverviewSection = useCallback(
        () => (
            <>
                <h4>OVERVIEW</h4>
                <Typography>
                    The <b>In Progress</b> state allows users to actively verify physical assets against the digital inventory.
                </Typography>
                <Typography>In this state, users can:</Typography>
                <List className={styles['unordered-list']}>
                    <ListItemText className={styles['list-item']}>
                        <span>
                            Search for and <b>select assets</b> from the inventory list.
                        </span>
                    </ListItemText>
                    <ListItemText className={styles['list-item']}>
                        <span>
                            <b>Add new assets</b> if not found in the inventory.
                        </span>
                    </ListItemText>
                    <ListItemText className={styles['list-item']}>
                        <span>
                            <b>Mark items</b> as either <b>Matched</b> or <b>Missed</b> to reflect their physical presence.
                        </span>
                    </ListItemText>
                </List>
                <Typography>
                    Once an asset is marked, it will be moved to the <b>Yard Checked Items</b> section and prepared for remediation in the next state
                    (Review).
                </Typography>
            </>
        ),
        []
    );

    const renderControlsSection = useCallback(
        () => (
            <>
                <h4>CONTROLS</h4>
                <Table className={styles['controls-table']}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Actions</TableCell>
                            <TableCell>Description</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                            <TableCell>Asset Search Bar</TableCell>
                            <TableCell>Search existing assets in the current inventory.</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>
                                <CustomButton customVariant="success">Add Item</CustomButton>
                            </TableCell>
                            <TableCell>Add a new asset if it cannot be found in the current inventory.</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>Inventory Items table</TableCell>
                            <TableCell>List of current inventory assets that can be verified.</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>Bulk Select Checkbox</TableCell>
                            <TableCell>Allow to select multiple assets for marking at a time.</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>Yard Check Items table</TableCell>
                            <TableCell>List of inventory assets that have been added to the yard check.</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>
                                <MoreVertIcon size="small" className={styles['inlined-icon']} /> Edit
                            </TableCell>
                            <TableCell>Edit the information of the matched assets.</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>
                                <MoreVertIcon size="small" className={styles['inlined-icon']} /> Remove
                            </TableCell>
                            <TableCell>Remove the assets from the yard check items list.</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>
                                <CustomButton customVariant="success">
                                    <CheckCircleIcon fontSize="small" style={{ marginRight: 4 }} />
                                    Matched
                                </CustomButton>
                            </TableCell>
                            <TableCell>Mark selected item(s) as physically present correctly in the inventory.</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>
                                {' '}
                                <CustomButton customVariant="error">
                                    <CancelIcon fontSize="small" style={{ marginRight: 4 }} />
                                    Missed
                                </CustomButton>
                            </TableCell>
                            <TableCell>Mark selected item(s) as missing from the yard.</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>
                                <CustomButton customVariant="success">Review</CustomButton>
                            </TableCell>
                            <TableCell>Proceed to the next step after completing asset verification.</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>
                                <CustomButton customVariant="error">Cancel</CustomButton>
                            </TableCell>
                            <TableCell>Exit and return to the Yard Check list with saving progress.</TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </>
        ),
        []
    );

    const renderStatusSection = useCallback(
        () => (
            <>
                <h4>STATUS INDICATOR</h4>
                <Table className={styles['controls-table']}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Status</TableCell>
                            <TableCell>Description</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                            <TableCell>
                                <YardCheckStatusChip status={MATCHED} />
                            </TableCell>
                            <TableCell>The asset exists in both the physical yard and the digital inventory.</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>
                                <YardCheckStatusChip status={MISSING} />
                            </TableCell>
                            <TableCell>The asset is recorded in the digital inventory but not found in the yard.</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>
                                {' '}
                                <YardCheckStatusChip status={UPDATED} />
                            </TableCell>
                            <TableCell>The asset is present and matched, but its information has been modified.</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>
                                {' '}
                                <YardCheckStatusChip status={NEW} />
                            </TableCell>
                            <TableCell>The asset is found in the yard but not recorded in the digital inventory.</TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
                <Alert severity="info" sx={{ paddingTop: 1, alignItems: 'center' }}>
                    Once a <CustomButton customVariant="success">Review</CustomButton> is clicked, all remaining items in the Inventory Items list
                    will be automatically marked as Missed.
                </Alert>
            </>
        ),
        []
    );

    const renderInstructionContent = useCallback((key) => {
        switch (key) {
            case 'searchAndAdd':
                return (
                    <Box className={styles['instruction-child-container']}>
                        <List className={styles['unordered-list']}>
                            <ListItemText>
                                1. You can type keywords in Asset Search Combo Box. The value can be anything related to checking Asset.
                            </ListItemText>
                            <ListItemText>
                                2. Lasta will lookup to get matching Assets List if possible and display all of them as a list of suggestions.
                            </ListItemText>
                            <ListItemText>
                                3. You can either choose one asset in the suggestions list or directly select multiple assets in the Inventory Items
                                list with the preference status to bring them to the Yard Check Items list.
                            </ListItemText>
                        </List>
                    </Box>
                );
            case 'manuallyAdd':
                return (
                    <Box className={styles['instruction-child-container']}>
                        <List className={styles['unordered-list']}>
                            <ListItemText>
                                1. If no matching Asset can be found with the given keywords, you can add a completely new asset by clicking on the{' '}
                                <CustomButton customVariant="success">Add Item</CustomButton>.
                            </ListItemText>
                            <ListItemText>
                                2. Once created, a new item will be visible on the Yard Check Items list with the Added status.
                            </ListItemText>
                        </List>
                    </Box>
                );
            case 'removeItems':
                return (
                    <Box className={styles['instruction-child-container']}>
                        <List className={styles['unordered-list']}>
                            <ListItemText>
                                1. Click on <MoreVertIcon size="small" className={styles['inlined-icon']} /> menu then choose “Remove” to open remove
                                confirmation prompt.
                            </ListItemText>
                            <ListItemText>
                                2. Click <CustomButton customVariant="success">Yes</CustomButton> to confirm removing the Item from the Yard Check
                                Items List, this action cannot be undone.
                            </ListItemText>
                            <ListItemText>
                                3. Click <CustomButton customVariant="error">Cancel</CustomButton> to close the confirmation prompt.
                            </ListItemText>
                        </List>
                    </Box>
                );
            case 'updateInfo':
                return (
                    <Box className={styles['instruction-child-container']}>
                        <List className={styles['unordered-list']}>
                            <ListItemText>
                                1. Double-click on an item in the Yard Check Items List or click on{' '}
                                <MoreVertIcon size="small" className={styles['inlined-icon']} /> the menu and choose <b>Edit</b>. (You can only edit
                                matched item).
                            </ListItemText>
                            <ListItemText>2. A modal popup will appear, allowing you to update the item’s information.</ListItemText>
                            <ListItemText>3. Once updated, an asset&apos;s status will be changed from matched to updated.</ListItemText>
                        </List>
                    </Box>
                );
            default:
                return null;
        }
    }, []);

    const renderInstructionsSection = useCallback(
        () => (
            <>
                <h4>INSTRUCTIONS</h4>
                <Box className={styles['instructions-accordion-container']}>
                    {instructions.map((instruction, index) => (
                        <Accordion key={instruction.key} defaultExpanded={index === 0} disableGutters className={styles['instruction-accordion']}>
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                aria-controls={`${instruction.key}-content`}
                                id={`${instruction.key}-header`}
                                className={styles['instruction-accordion-summary']}
                            >
                                <Typography className={styles['instruction-header']}>{instruction.title}</Typography>
                            </AccordionSummary>
                            <AccordionDetails className={styles['instruction-accordion-details']}>
                                {renderInstructionContent(instruction.key)}
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </Box>
            </>
        ),
        [instructions, renderInstructionContent]
    );

    const renderDivider = useCallback(() => <Divider className={styles['divider']} />, []);

    const renderFooter = useCallback(
        () => (
            <Stack className={styles['footer']}>
                <span>
                    For further support, email us at{' '}
                    <Link href="mailto:cs@birdseye.ca" underline="none">
                        cs@birdseye.ca
                    </Link>
                    .
                </span>
            </Stack>
        ),
        []
    );

    return (
        <Stack className={styles['help-dialog-container']}>
            <Box className={styles['help-dialog-content-container']}>
                {renderOverviewSection()}
                {renderDivider()}
                {renderControlsSection()}
                {renderDivider()}
                {renderStatusSection()}
                {renderDivider()}
                {renderInstructionsSection()}
            </Box>
            {renderFooter()}
        </Stack>
    );
};

export default CheckingContent;
